import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useConversationHub } from "../../../hooks/useConversationHub";
import MessageBubble from "../components/MessageBubble";
import {
  LODA_WELCOME_MESSAGE,
  LODA_WELCOME_MESSAGE_ENGLISH_TEXT,
  LODA_WELCOME_MESSAGE_SWEDISH_TEXT,
  LARA_WELCOME_MESSAGE,
  LARA_WELCOME_MESSAGE_ENGLISH_TEXT,
  LARA_WELCOME_MESSAGE_SWEDISH_TEXT,
} from "../../../models/conversation/Message";
import { shouldHideTempAiMessageInList } from "@/lib/streamMessageState";
import useWorkspace from "../../../hooks/useWorkspace";
import { SendMessageContextFilter } from "../../../services/messageService";
import ChatEmptyState from "./empty-state/ChatEmptyState";
import Workspace, { WorkspaceFolder } from "@/models/workspace/Workspace";
import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";
import { convertFiltersToSearchOptions } from "../utils/PineconeQueries";
import { cn } from "@/lib/utils";
import { useDocumentReferenceStore } from "@/stores/document-reference.store";
import { useContent } from "@/hooks/useContent";
import ChatInput from "../components/ChatInput";
import ChainOfThoughtBubble from "../components/ChainOfThoughtBubble";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import React from "react";
import { Alert, AlertDescription, AlertTitle } from "@capacit-com/lara-law-shared-ui";
import { AlertCircle, Clock } from "lucide-react";
import {
  cleanupWorkspaceContext,
  computeConversationFlags,
  initOrMergeContextForWorkspace,
} from "./chat-container-helpers";
import {
  buildConversationWorkspaceDocuments,
  MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS,
} from "@/lib/buildConversationWorkspaceDocuments";
import { showWarningToast } from "@/components/custom-toasts/toasts";
import { Domain, getDomain, getDomainName } from "@/utils/domain";
import NoContextWarning, {
  shouldShowcontext,
} from "../components/dialogs/NoContextWarningDialog";
import PiiReviewDialog from "../components/pii/PiiReviewDialog";
import { usePiiReview } from "../hooks/usePiiReview";
import usePiiWhitelist from "@/hooks/usePiiWhitelist";
import usePiiFeatureEnabled from "@/hooks/usePiiFeatureEnabled";
import { useSourceSelection } from "../hooks/useSourceSelection";
import {
  computeIsAtBottom,
  computeMaxScrollTop,
  getChatScrollState,
  resolveRestoredScrollTop,
  saveChatScrollState,
} from "@/lib/chatScrollMemory";

type Props = {
  /**
   * A conversation named by the URL is on its way back. Until it lands there is
   * nothing to show but an empty chat, and rendering the greeting screen for
   * that moment reads as "your conversation is gone" (LAR-405).
   */
  isRestoringConversation: boolean;
};

const ChatContainer: FC<Props> = ({ isRestoringConversation }) => {
  const [allWorkspaces, setAllWorkspaces] = useState<Workspace[]>([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] =
    useState<boolean>(false);
  const { reset } = useDocumentReferenceStore();
  const { t } = useContent();
  const {
    selectedConversation,
    latestMessage,
    sendMessage,
    isLoadingMessages,
    selectConversation,
  } = useConversationHub();
  const { useQueryAllWorkspaces } = useWorkspace();
  const {
    workspaceId,
    setWorkspaceId,
    resetFilters,
    workspaceContext,
    setWorkspaceContext,
  } = useNewConversationFiltersStore();

  // Client-side PII sanitation gate (LAR-142). The tenant whitelist (LAR-27 #3)
  // suppresses approved soft matches; degrades to no exemptions if unavailable.
  // Whitelist fetch and gate are inert while the PII-Redaction flag is off
  // (usePiiReview bypasses scanning on its own via the same flag).
  const piiFeatureEnabled = usePiiFeatureEnabled();
  const { useQueryWhitelist } = usePiiWhitelist();
  const whitelistQuery = useQueryWhitelist(piiFeatureEnabled);
  const whitelistTerms = useMemo(
    () => whitelistQuery.data?.terms.map((term) => term.normalized) ?? [],
    [whitelistQuery.data]
  );
  const piiReview = usePiiReview(whitelistTerms);

  const chatWindowRef = useRef<HTMLDivElement | null>(null);
  const previousConversationIdRef = useRef<string | undefined>(undefined);
  const lastRestoredConversationIdRef = useRef<string | null>(null);
  const isRestoringScrollRef = useRef(false);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(true);
  const [isWorkspaceDeleted, setIsWorkspaceDeleted] = useState<boolean>(false);
  const [isWorkspaceDocumentsMissing, setIsWorkspaceDocumentsMissing] =
    useState<boolean>(false);
  const [isContentOutdated, setIsContentOutdated] = useState<boolean>(false);
  const fetchedWorkspaces = useQueryAllWorkspaces();

  const isConversationLocked = useMemo(() => {
    if (!selectedConversation?.createdAt) {
      return false;
    }

    const cutoffDate = new Date("2025-11-11T00:00:00.000Z");
    const conversationDate = new Date(selectedConversation.createdAt);

    return conversationDate < cutoffDate;
  }, [selectedConversation]);
  const [showNoContextWarning, setShowNoContextWarning] =
    useState<boolean>(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const chatInputValueRef = useRef<((value: string) => void) | null>(null);
  useSourceSelection();

  const lastMessage = latestMessage
    ? latestMessage
    : selectedConversation?.messages
      ? selectedConversation?.messages[selectedConversation.messages.length - 1]
      : null;

  const handleSendMessage = (text: string, skipWarning?: boolean) => {
    if (isWorkspaceDeleted || isWorkspaceDocumentsMissing) return;

    // Get fresh values from the store to avoid using stale closure values
    // This is important when sources are selected through the NoContextWarning dialog
    const currentState = useNewConversationFiltersStore.getState();
    const currentFilters = currentState.filters;
    const currentWorkspaceContext = currentState.workspaceContext;
    const currentSelectedSource = currentState.selectedSource;

    // Check if there's no context
    const hasFilters = Object.keys(currentFilters).length > 0;
    const hasWorkspaceContext = Object.keys(currentWorkspaceContext).length > 0;
    const hasNoContext = !hasFilters && !hasWorkspaceContext;

    // Once a conversation exists, don't show the warning for subsequent messages
    if (
      !selectedConversation &&
      hasNoContext &&
      !skipWarning &&
      !pendingMessage &&
      shouldShowcontext()
    ) {
      setPendingMessage(text);
      setShowNoContextWarning(true);
      return;
    }

    // If we have a pending message (from warning dialog) or user clicked continue anyway, send it
    const messageToSend = pendingMessage || text;
    setPendingMessage(null);
    setShowNoContextWarning(false);

    const contextFilters: SendMessageContextFilter[] = [];
    const searchOptions = convertFiltersToSearchOptions(
      currentSelectedSource,
      currentWorkspaceContext
    );

    for (const workspaceId in currentWorkspaceContext) {
      const selectedIds = currentWorkspaceContext[workspaceId];
      contextFilters.push({
        workspaceId,
        documentIdsWithRevisionNumbers: selectedIds,
      });
    }

    const workspaceDocuments = buildConversationWorkspaceDocuments(
      currentWorkspaceContext,
      allWorkspaces
    );

    // Scan for PII before the prompt leaves the browser; on clean or confirmed,
    // send the (possibly redacted) text. Fail-closed if scanning errors.
    // The input is cleared HERE — only once the send actually proceeds — so a
    // cancelled PII review leaves the user's prompt intact (LAR-27 v2 UI #1).
    void piiReview.guardSend(messageToSend, (finalText, overrides) => {
      chatInputValueRef.current?.("");
      sendMessage(finalText, searchOptions, contextFilters, workspaceDocuments, {
        piiOverride: overrides,
      });
    });
  };

  const handleFirstMessageWithNoContext = (text: string) => {
    // Get fresh values from the store
    const currentState = useNewConversationFiltersStore.getState();
    const currentFilters = currentState.filters;
    const currentWorkspaceContext = currentState.workspaceContext;

    const hasFilters = Object.keys(currentFilters).length > 0;
    const hasWorkspaceContext = Object.keys(currentWorkspaceContext).length > 0;
    const hasNoContext = !hasFilters && !hasWorkspaceContext;

    if (hasNoContext && shouldShowcontext()) {
      // No context - show warning (don't send, input will be preserved)
      setPendingMessage(text);
      setShowNoContextWarning(true);
    } else {
      // Has context - send directly (or no warning needed)
      handleSendMessage(text);
    }
  };

  const filterWorkspacesNoFiles = (workspaces: Workspace[]) => {
    const hasFilesRecursively = (folders: WorkspaceFolder[]): boolean => {
      return folders.some((folder) => {
        if (folder.documents.length > 0) return true;
        if (folder.folders.length > 0)
          return hasFilesRecursively(folder.folders);
        return false;
      });
    };

    return workspaces.filter((workspace) => {
      const hasTopLevelFiles = workspace.documents.length > 0;
      const hasFilesInFolders = hasFilesRecursively(workspace.folders);
      return hasTopLevelFiles || hasFilesInFolders;
    });
  };

  useEffect(() => {
    const chatWindow = chatWindowRef.current;
    const handleScroll = () => {
      if (chatWindow) {
        setIsAtBottom(computeIsAtBottom(chatWindow));
      }
    };
    if (chatWindow) chatWindow.addEventListener("scroll", handleScroll);

    return () => {
      if (chatWindow) chatWindow.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const prevId = previousConversationIdRef.current;
    const nextId = selectedConversation?.id;
    const container = chatWindowRef.current;
    if (prevId && !nextId && container) {
      saveChatScrollState(prevId, container);
    }
    if (nextId) {
      isRestoringScrollRef.current = true;
      const saved = getChatScrollState(nextId);
      setIsAtBottom(saved?.isAtBottom ?? true);
    }
    previousConversationIdRef.current = nextId;
    lastRestoredConversationIdRef.current = null;
  }, [selectedConversation?.id]);

  useEffect(() => {
    if (!selectedConversation?.id || isLoadingMessages.isLoading) return;

    const conversationId = selectedConversation.id;
    if (lastRestoredConversationIdRef.current === conversationId) return;

    let cancelled = false;
    let outerFrameId = 0;
    let innerFrameId = 0;
    let resetFrameId = 0;

    const applyScroll = () => {
      if (cancelled || previousConversationIdRef.current !== conversationId) {
        return;
      }

      const container = chatWindowRef.current;
      if (!container) return;

      lastRestoredConversationIdRef.current = conversationId;
      isRestoringScrollRef.current = true;

      const saved = getChatScrollState(conversationId);
      if (saved) {
        setIsAtBottom(saved.isAtBottom);
        container.scrollTop = resolveRestoredScrollTop(saved, container);
      } else {
        setIsAtBottom(true);
        container.scrollTop = computeMaxScrollTop(container);
      }

      resetFrameId = requestAnimationFrame(() => {
        if (!cancelled) {
          isRestoringScrollRef.current = false;
        }
      });
    };

    outerFrameId = requestAnimationFrame(() => {
      if (cancelled) return;
      innerFrameId = requestAnimationFrame(applyScroll);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(outerFrameId);
      cancelAnimationFrame(innerFrameId);
      cancelAnimationFrame(resetFrameId);
    };
  }, [selectedConversation?.id, isLoadingMessages.isLoading]);

  useEffect(() => {
    if (isRestoringScrollRef.current) return;
    if (chatWindowRef.current && isAtBottom) {
      chatWindowRef.current.scrollTop = computeMaxScrollTop(
        chatWindowRef.current
      );
    }
  }, [isAtBottom, lastMessage]);

  useEffect(() => {
    if (!(fetchedWorkspaces.isFetched && fetchedWorkspaces.data)) return;
    // Step 1: Set allWorkspaces state
    const allWs = fetchedWorkspaces.data as Workspace[];

    // Recursive function to sort documents and folders at all levels
    const sortFolderContents = (folder: WorkspaceFolder) => {
      // Sort documents: indexed first, then by name
      folder.documents.sort((a, b) => {
        const aIndexed = a.revisions[a.activeRevision - 1]?.isIndexed ? 1 : 0;
        const bIndexed = b.revisions[b.activeRevision - 1]?.isIndexed ? 1 : 0;
        if (aIndexed !== bIndexed) {
          return bIndexed - aIndexed;
        }
        return a.name.localeCompare(b.name);
      });

      // Sort folders by name
      folder.folders.sort((a, b) => a.name.localeCompare(b.name));

      // Recursively sort subfolders
      folder.folders.forEach(sortFolderContents);
    };

    for (const workspace of allWs) {
      // Sort root-level documents
      workspace.documents.sort((a, b) => {
        const aIndexed = a.revisions[a.activeRevision - 1]?.isIndexed ? 1 : 0;
        const bIndexed = b.revisions[b.activeRevision - 1]?.isIndexed ? 1 : 0;
        if (aIndexed !== bIndexed) {
          // Indexed first, unindexed last
          return bIndexed - aIndexed;
        }
        return a.name.localeCompare(b.name);
      });

      // Sort root-level folders
      workspace.folders.sort((a, b) => a.name.localeCompare(b.name));

      // Recursively sort all subfolders
      workspace.folders.forEach(sortFolderContents);
    }
    setAllWorkspaces(
      allWs.sort(
        (a, b) =>
          new Date(b.createdAt || "").getTime() -
          new Date(a.createdAt || "").getTime()
      )
    );

    // Step 2: Clean up workspaceContext
    const { newContext, needsCleanup } = cleanupWorkspaceContext(
      allWs,
      workspaceContext
    );

    // Step 3: Apply status flags if the selectedConversation is affected by deletions/updates
    const flags = computeConversationFlags(selectedConversation, allWs);
    setIsWorkspaceDeleted(flags.isWorkspaceDeleted);
    setIsWorkspaceDocumentsMissing(flags.isWorkspaceDocumentsMissing);
    setIsContentOutdated(flags.isContentOutdated);

    // Persist any necessary cleanup to the store
    if (needsCleanup) {
      setWorkspaceContext(newContext);
    }

    // Last Step: Initialize/merge context when a workspaceId is selected ("Chat med Workspace")
    if (workspaceId) {
      resetFilters();
      const { updatedContext, changed, trimmedAway } =
        initOrMergeContextForWorkspace(workspaceId, allWs, newContext);
      if (changed) setWorkspaceContext(updatedContext);
      if (trimmedAway > 0) {
        showWarningToast(
          `Kun de første ${MAX_CONVERSATION_WORKSPACE_DOCUMENT_SNAPSHOTS} dokumenter kan bruges i konteksten. Tilpas dit valg i workspace-vælgeren hvis du mangler filer.`,
          "Maks. dokumenter i kontekst"
        );
      }
      reset();
      selectConversation(undefined);
      setWorkspaceId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchedWorkspaces.data,
    fetchedWorkspaces.isFetched,
    selectedConversation,
    workspaceId,
  ]);
  useEffect(() => {
    setIsLoadingWorkspaces(fetchedWorkspaces.isPending);
  }, [fetchedWorkspaces.isPending]);

  const domainName = getDomainName(getDomain(window.location.hostname));

  return (
    <div
      className={cn("flex grow transition-all duration-300 overflow-x-hidden")}
    >
      <div className="h-full w-full flex justify-between flex-col bg-background">
        <div
          ref={chatWindowRef}
          data-chat-scroll-container="true"
          className={cn(
            "flex flex-col grow h-full overflow-y-auto gap-4 pt-2 pb-10",
            !selectedConversation
              ? "items-center justify-center px-4"
              : "pl-14 xl:pl-14 pr-4 xl:pr-8"
          )}
        >
          {isLoadingMessages.isLoading || isRestoringConversation ? (
            <div className="flex flex-col justify-center items-center h-full">
              <Spinner className="size-10 mx-auto" />
            </div>
          ) : !selectedConversation ? (
            <>
              <ChatEmptyState
                workspaces={filterWorkspacesNoFiles(allWorkspaces)}
                isLoadingWorkspaces={isLoadingWorkspaces}
                onPromptSelect={(text) => {
                  if (chatInputValueRef.current) {
                    chatInputValueRef.current(text);
                  }
                }}
                onSubmit={handleFirstMessageWithNoContext}
                disabled={isWorkspaceDeleted || isWorkspaceDocumentsMissing}
                setInputValueRef={chatInputValueRef}
                piiPreparing={!piiReview.modelReady}
                piiScanning={piiReview.isScanning}
              />
              <NoContextWarning
                workspaces={filterWorkspacesNoFiles(allWorkspaces)}
                isLoadingWorkspaces={isLoadingWorkspaces}
                open={showNoContextWarning}
                onContinue={() => {
                  // Don't clear here — handleSendMessage clears only once the send
                  // proceeds, so a following PII review can still be cancelled
                  // without losing the prompt (LAR-27 v2 UI #1).
                  if (pendingMessage) handleSendMessage(pendingMessage, true);
                }}
                onDismiss={() => {
                  setShowNoContextWarning(false);
                  setPendingMessage(null);
                }}
              />
            </>
          ) : (
            <>
              <h1 className="font-bold text-2xl -ml-10">
                {selectedConversation?.title}
              </h1>

              <MessageBubbleList />
              <LatestMessageBubble />
            </>
          )}
        </div>
        {selectedConversation && (
          <div className="flex flex-col justify-center items-center px-4 xl:px-8">
            {isConversationLocked ? (
              <div className="p-2 w-full">
                <Alert variant="default" className="mb-4 border-warning/30">
                  <AlertCircle className="h-4 w-4 text-warning" />
                  <AlertTitle className="text-warning">
                    Samtalen er låst
                  </AlertTitle>
                  <AlertDescription>
                    Denne samtale er baseret på vores gamle datagrundlag og er
                    derfor låst. Du kan ikke sende nye beskeder i denne samtale.
                    For at fortsætte samtalen, skal du starte en ny samtale.
                  </AlertDescription>
                </Alert>
              </div>
            ) : isWorkspaceDeleted || isWorkspaceDocumentsMissing ? (
              <div className="p-2">
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>
                    Et eller flere af dine dokumenter er blevet slettet siden
                    sidst
                  </AlertTitle>
                  <AlertDescription>
                    Siden denne samtale blev oprettet er et eller flere af
                    workspacet's dokumenter blevet slettet. {domainName}'s svar
                    i samtalen er baseret på det tidligere indhold af
                    workspacet, og {domainName}'s svar kan derfor være
                    forældede. For at få {domainName}'s vurdering af det
                    opdaterede indhold i workspacet, skal du starte en ny
                    samtale.
                  </AlertDescription>
                </Alert>
              </div>
            ) : (
              isContentOutdated && (
                <div className="p-2">
                  <Alert variant="default" className="mb-4 border-warning/30">
                    <Clock className="h-4 w-4 text-warning" />
                    <AlertTitle className="text-warning">
                      Et eller flere af dine dokumenter er blevet opdateret
                      siden sidst
                    </AlertTitle>
                    <AlertDescription>
                      Siden denne samtale blev oprettet er der foretaget
                      ændringer i ét eller flere af workspacet's dokumenter.{" "}
                      {domainName}'s svar i samtalen er baseret på den tidligere
                      udgave af dokumenterne, og {domainName}'s svar kan derfor
                      være forældede. For at få {domainName}'s vurdering af det
                      opdaterede indhold i workspacet's dokumenter, kan du blot
                      stille dine spørgsmål igen i en ny samtale.
                    </AlertDescription>
                  </Alert>
                </div>
              )
            )}
            {!piiReview.modelReady && (
              <span className="w-full pb-1 text-xs text-muted-foreground animate-pulse">
                Forbereder sikkerhedstjek…
              </span>
            )}
            <ChatInput
              onSubmit={handleSendMessage}
              isScanning={piiReview.isScanning}
              inputTestId="chat-input"
              submitTestId="chat-send-button"
              disabled={
                isConversationLocked ||
                isWorkspaceDeleted ||
                isWorkspaceDocumentsMissing
              }
              selectedConversation={selectedConversation ?? undefined}
              setInputValueRef={chatInputValueRef}
              workspaces={filterWorkspacesNoFiles(allWorkspaces)}
              isLoadingWorkspaces={isLoadingWorkspaces}
              // Always defer clearing the input to the send-proceeds path in
              // handleSendMessage, so a cancelled PII review preserves the prompt
              // (LAR-27 v2 UI #1). Subsequent messages in an existing conversation
              // never show the no-context warning.
              onPendingSubmit={() => true}
            />
            <NoContextWarning
              workspaces={filterWorkspacesNoFiles(allWorkspaces)}
              isLoadingWorkspaces={isLoadingWorkspaces}
              open={showNoContextWarning}
              onContinue={() => {
                // Don't clear here — handleSendMessage clears only once the send
                // proceeds, so a following PII review can still be cancelled
                // without losing the prompt (LAR-27 v2 UI #1).
                if (pendingMessage) handleSendMessage(pendingMessage, true);
              }}
              onDismiss={() => {
                setShowNoContextWarning(false);
                setPendingMessage(null);
              }}
            />
            <span className="text-xs text-foreground/30 py-1">
              {t("chat.disclaimer")}
            </span>
          </div>
        )}
        {/* Rendered outside the selectedConversation block so the PII gate also
            covers the very first message of a new conversation (LAR-27 v2). */}
        <PiiReviewDialog
          open={piiReview.reviewOpen}
          originalText={piiReview.reviewText}
          spans={piiReview.reviewSpans}
          onCancel={piiReview.cancelReview}
          onConfirm={piiReview.confirmReview}
        />
      </div>
    </div>
  );
};

export default ChatContainer;

const MessageBubbleList = React.memo(() => {
  const { selectedConversation, selectedLanguage, latestMessage } =
    useConversationHub();

  const domain = getDomain(window.location.hostname);

  const getWelcomeMessage = () => {
    if (domain === Domain.LODA) {
      return selectedLanguage?.code === "da"
        ? LODA_WELCOME_MESSAGE
        : selectedLanguage?.code === "sw"
          ? { ...LODA_WELCOME_MESSAGE, text: LODA_WELCOME_MESSAGE_SWEDISH_TEXT }
          : {
              ...LODA_WELCOME_MESSAGE,
              text: LODA_WELCOME_MESSAGE_ENGLISH_TEXT,
            };
    } else {
      return selectedLanguage?.code === "da"
        ? LARA_WELCOME_MESSAGE
        : selectedLanguage?.code === "sw"
          ? { ...LARA_WELCOME_MESSAGE, text: LARA_WELCOME_MESSAGE_SWEDISH_TEXT }
          : {
              ...LARA_WELCOME_MESSAGE,
              text: LARA_WELCOME_MESSAGE_ENGLISH_TEXT,
            };
    }
  };

  return (
    <>
      <MessageBubble
        message={getWelcomeMessage()}
        bubbleTestId="welcome-message"
      />
      {selectedConversation?.messages && (
        <>
          {selectedConversation.messages
            .filter(
              (message) =>
                !shouldHideTempAiMessageInList(
                  message.id,
                  latestMessage?.conversationId,
                  selectedConversation.id
                )
            )
            .map((message, index) => (
            <div
              key={message.id + index}
              className={cn(!message.human && "mt-1 mb-8 last:mb-4")}
            >
              <ChainOfThoughtBubble
                message={message}
                bubbleTestId={`message-${index}`}
              />
              <MessageBubble
                message={message}
                bubbleTestId={`message-${index}`}
              />
            </div>
          ))}
        </>
      )}
    </>
  );
});

const LatestMessageBubble = React.memo(() => {
  const { latestMessage, selectedConversation } = useConversationHub();

  return (
    <>
      {latestMessage !== null &&
        latestMessage.conversationId === selectedConversation?.id && (
          <div
            className={cn(!latestMessage.human && "mt-1 mb-8 last:mb-4")}
          >
            <ChainOfThoughtBubble
              message={latestMessage}
              bubbleTestId="latest-message"
            />
            <MessageBubble
              message={latestMessage}
              bubbleTestId="latest-message"
            />
          </div>
        )}
    </>
  );
});
